/**
 * Invoice Form Helpers
 *
 * Utility functions, constants, and type definitions for the invoice form.
 */

import type { Party, Address } from '@/gen/proto'

import { getCachedCountries } from '../../../services/geolocation-service'
import { getCountryFlagSafe } from '../../../utils/formatters'

import type { InvoiceType } from '../../../types/billing'
import type { SelectOption, SearchableSelectOption } from '../../ui'

// =============================================================================
// Types
// =============================================================================

export interface LineItem {
  id: string
  /** Catalog item UUID — links the line to a stocked product so posting can
   *  record an inventory movement. Empty for free-text (non-catalog) lines. */
  itemId?: string
  /** Explicit GL account override for this line's journal-entry posting.
   *  Empty means resolve normally (item-based, then generic fallback). */
  accountId?: string
  itemCode?: string
  description: string
  hsnCode: string
  sacCode?: string
  quantity: string
  unit: string
  unitPrice: string
  discountPercent: string
  gstRate: string
  taxableAmount: number
  cgstAmount: number
  sgstAmount: number
  igstAmount: number
  totalAmount: number
}

export interface InvoiceTotals {
  subtotal: number
  discountAmount: number
  taxableAmount: number
  cgstAmount: number
  sgstAmount: number
  igstAmount: number
  cessAmount: number
  totalTax: number
  totalAmount: number
  roundOffAmount: number
  grandTotal: number
}

// =============================================================================
// Constants
// =============================================================================

export const SALES_INVOICE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'b2b', label: 'B2B Tax Invoice' },
  { value: 'export', label: 'Export Invoice' },
  { value: 'b2c', label: 'B2C Invoice' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'debit_note', label: 'Debit Note' },
]

export const PURCHASE_INVOICE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'vendor_bill', label: 'Vendor Bill' },
  { value: 'vendor_credit_note', label: 'Vendor Credit Note' },
]

export const GST_RATE_OPTIONS: SelectOption[] = [
  { value: '0', label: '0%' },
  { value: '5', label: '5%' },
  { value: '12', label: '12%' },
  { value: '18', label: '18%' },
  { value: '28', label: '28%' },
]

export const UNIT_OPTIONS: SelectOption[] = [
  { value: 'NOS', label: 'NOS' },
  { value: 'PCS', label: 'PCS' },
  { value: 'SET', label: 'SET' },
  { value: 'BOX', label: 'BOX' },
  { value: 'PKT', label: 'PKT' },
  { value: 'BAG', label: 'BAG' },
  { value: 'HRS', label: 'HRS' },
  { value: 'DAYS', label: 'DAYS' },
  { value: 'MON', label: 'MON' },
  { value: 'KGS', label: 'KGS' },
  { value: 'GMS', label: 'GMS' },
  { value: 'TON', label: 'TON' },
  { value: 'MTR', label: 'MTR' },
  { value: 'SQM', label: 'SQM' },
  { value: 'SQF', label: 'SQF' },
  { value: 'LTR', label: 'LTR' },
  { value: 'MLT', label: 'MLT' },
  { value: 'RLS', label: 'RLS' },
  { value: 'CTN', label: 'CTN' },
  { value: 'DOZ', label: 'DOZ' },
]

export const PAYMENT_TERM_OPTIONS: SelectOption[] = [
  { value: '0', label: 'Due on Receipt' },
  { value: '7', label: 'Net 7' },
  { value: '15', label: 'Net 15' },
  { value: '30', label: 'Net 30' },
  { value: '45', label: 'Net 45' },
  { value: '60', label: 'Net 60' },
  { value: '90', label: 'Net 90' },
  { value: 'custom', label: 'Custom' },
]

export const EXPORT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'goods', label: 'Physical Goods' },
  { value: 'services', label: 'Services/Digital' },
]

export const MODE_OF_TRANSPORT_OPTIONS: SelectOption[] = [
  { value: 'sea', label: 'Sea' },
  { value: 'air', label: 'Air' },
  { value: 'road', label: 'Road' },
  { value: 'rail', label: 'Rail' },
]

export const STANDARD_TERM_DAYS = ['0', '7', '15', '30', '45', '60', '90']

// =============================================================================
// ID Generation
// =============================================================================

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15)
}

// =============================================================================
// Line Item Factories
// =============================================================================

export function createEmptyLineItem(defaultGstRate = '18'): LineItem {
  return {
    id: generateId(),
    itemId: '',
    accountId: '',
    itemCode: '',
    description: '',
    hsnCode: '',
    sacCode: '',
    quantity: '1',
    unit: 'NOS',
    unitPrice: '0',
    discountPercent: '0',
    gstRate: defaultGstRate,
    taxableAmount: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalAmount: 0,
  }
}

// =============================================================================
// Date Utilities
// =============================================================================

/**
 * Parse a date-only string (YYYY-MM-DD) as local midnight, not UTC.
 * JS spec: `new Date('2025-05-31')` → UTC midnight → wrong day in IST.
 * Appending T00:00:00 forces local timezone interpretation.
 */
export function parseLocalDate(s: string): Date {
  return new Date(s.includes('T') ? s : `${s}T00:00:00`)
}

/** Format Date as YYYY-MM-DD using local timezone (not UTC) */
export function toLocalDateString(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// =============================================================================
// Payment Terms Utilities
// =============================================================================

export function creditDaysToTermString(days: number): string {
  if (days === 0) { return 'Due on Receipt' }
  return `Net ${days}`
}

export function creditDaysToSelectValue(days: number): string {
  const dayStr = String(days)
  return STANDARD_TERM_DAYS.includes(dayStr) ? dayStr : 'custom'
}

export function parsePaymentTermsToDays(terms: string): { selectValue: string; customDays: string } {
  if (terms === 'Due on Receipt') { return { selectValue: '0', customDays: '' } }
  const match = terms.match(/Net\s+(\d+)/)
  if (match?.[1]) {
    const days = parseInt(match[1], 10)
    const selectValue = creditDaysToSelectValue(days)
    return { selectValue, customDays: selectValue === 'custom' ? match[1] : '' }
  }
  return { selectValue: '30', customDays: '' }
}

// =============================================================================
// Country Lookup Utilities
// =============================================================================

/** Look up country code by country name from cached countries */
export function lookupCountryCode(countryName: string): string | undefined {
  const cachedCountries = getCachedCountries()
  if (!cachedCountries || cachedCountries.length === 0) { return undefined }

  const normalizedSearch = countryName.toLowerCase().trim()
  const match = cachedCountries.find(
    (c) =>
      c.name.toLowerCase() === normalizedSearch ||
      c.identifier.toLowerCase() === normalizedSearch,
  )

  return match?.identifier
}

/** Look up country name by code from cached countries */
export function lookupCountryName(countryCode: string): string {
  const cachedCountries = getCachedCountries()
  if (!cachedCountries || cachedCountries.length === 0) { return countryCode }
  const match = cachedCountries.find(
    (c) => c.identifier.toUpperCase() === countryCode.toUpperCase(),
  )
  return match?.name ?? countryCode
}

// =============================================================================
// Party Helpers
// =============================================================================

function getCountryCodeFromAddress(addr: Address | undefined): string | undefined {
  if (!addr) { return undefined }
  if (addr.countryCode) { return addr.countryCode.toUpperCase() }
  if (addr.country) {
    const code = lookupCountryCode(addr.country)
    if (code) { return code.toUpperCase() }
  }
  return undefined
}

/** Get a party's country code from its best available address */
export function getPartyCountryCode(party: Party, orgCountryCode: string): string {
  const billingCountry = getCountryCodeFromAddress(party.billingAddress)
  if (billingCountry) { return billingCountry }

  const shippingCountry = getCountryCodeFromAddress(party.shippingAddress)
  if (shippingCountry) { return shippingCountry }

  return orgCountryCode.toUpperCase()
}

/** Convert parties list to SearchableSelect options, optionally filtered by invoice type */
export function partiesToOptions(
  parties: Party[],
  orgCountryCode: string = 'IN',
  invoiceType?: InvoiceType,
): SearchableSelectOption<string>[] {
  let filteredParties = parties
  if (invoiceType === 'export') {
    filteredParties = parties.filter(
      (party) => getPartyCountryCode(party, orgCountryCode) !== orgCountryCode.toUpperCase(),
    )
  } else if (invoiceType === 'b2b') {
    filteredParties = parties.filter(
      (party) => getPartyCountryCode(party, orgCountryCode) === orgCountryCode.toUpperCase(),
    )
  }

  return filteredParties.map((party) => {
    const displayAddr = party.billingAddress ?? party.shippingAddress
    const partyCountry = getPartyCountryCode(party, orgCountryCode)
    const isInternational = partyCountry !== orgCountryCode.toUpperCase()
    const flag = getCountryFlagSafe(partyCountry)
    const displayName = party.companyVendorCode
      ? `${party.name} (${party.companyVendorCode})`
      : party.name

    const state = displayAddr?.state
    const country = displayAddr?.country
    let locationLine = ''
    if (state && country) {
      locationLine = `${state}, ${country}`
    } else if (country) {
      locationLine = country
    } else if (state) {
      locationLine = state
    }

    const option: SearchableSelectOption<string> = {
      value: party.id,
      label: displayName,
      description: locationLine,
      data: {
        party,
        flag,
        isInternational,
        countryCode: partyCountry,
        stateCode: displayAddr?.stateCode,
      },
    }

    return option
  })
}

// =============================================================================
// Label Helpers
// =============================================================================

export function getBillingAddressLabel(invoiceType: InvoiceType, isVendor: boolean): string {
  if (invoiceType === 'export') { return 'Client Address' }
  if (isVendor) { return 'Supplier Address' }
  return 'Billing Address'
}

export function getShippingAddressModalTitle(invoiceType: InvoiceType, isVendor: boolean): string {
  if (invoiceType === 'export') { return 'Select Consignee Address' }
  if (isVendor) { return 'Select Delivery Address' }
  return 'Select Shipping Address'
}

export function getSubmitButtonLabel(isSubmitting: boolean, isEditing: boolean): string {
  if (isSubmitting) { return 'Saving...' }
  if (isEditing) { return 'Update Invoice' }
  return 'Create Invoice'
}

export function getPageTitle(isEditing: boolean, isVendor: boolean): string {
  if (isEditing) { return isVendor ? 'Edit Vendor Bill' : 'Edit Invoice' }
  return isVendor ? 'New Vendor Bill' : 'New Invoice'
}

export function getPageDescription(isEditing: boolean, isVendor: boolean): string {
  if (isEditing) { return isVendor ? 'Update vendor bill details' : 'Update invoice details' }
  return isVendor ? 'Create a new vendor bill' : 'Create a new invoice for your customer'
}

// =============================================================================
// Quantity Decimal Places
// =============================================================================

const QTY_DECIMAL_PLACES: Record<string, number> = {
  // Count units — whole numbers only
  NOS: 0, PCS: 0, SET: 0, BOX: 0, PKT: 0, BAG: 0, CTN: 0, DOZ: 0, RLS: 0,
  // Time / service units
  HRS: 2, DAYS: 2, MON: 2,
  // Weight, volume, area — continuous measurement
  KGS: 3, GMS: 3, TON: 3, MTR: 3, SQM: 3, SQF: 3, LTR: 3, MLT: 3,
}

export function getQtyDecimalPlaces(unit: string): number {
  return QTY_DECIMAL_PLACES[unit] ?? 2
}

export function formatQtyDisplay(qty: string, unit: string): string {
  const num = parseFloat(qty)
  if (isNaN(num)) { return qty }
  return parseFloat(num.toFixed(getQtyDecimalPlaces(unit))).toString()
}
